"use client";

import React from "react";
import {
  ToastContainer,
  toast as toastify,
  ToastOptions,
} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface ToastContextType {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined
);

const DEFAULT_TOAST_OPTIONS: ToastOptions = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

export const ToastProvider = React.memo(function ToastProvider({
  children,
}: React.PropsWithChildren) {

  const success = React.useCallback(
    (message: string, options?: ToastOptions) => {
      toastify.success(message, { ...DEFAULT_TOAST_OPTIONS, ...options });
    },
    []
  );

  const error = React.useCallback((message: string, options?: ToastOptions) => {
    toastify.error(message, { ...DEFAULT_TOAST_OPTIONS, ...options });
  }, []);

  const info = React.useCallback((message: string, options?: ToastOptions) => {
    toastify.info(message, { ...DEFAULT_TOAST_OPTIONS, ...options });
  }, []);

  const warning = React.useCallback(
    (message: string, options?: ToastOptions) => {
      toastify.warning(message, { ...DEFAULT_TOAST_OPTIONS, ...options });
    },
    []
  );

  const contextValue = React.useMemo(
    () => ({
      success,
      error,
      info,
      warning,
    }),
    [success, error, info, warning]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer limit={3} closeButton={true} />
    </ToastContext.Provider>
  );
});

const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export type OptimizedToastType = ReturnType<typeof useOptimizedToast>;

export const useOptimizedToast = () => {
  const toast = useToast();
  const toastTimeoutRef = React.useRef<NodeJS.Timeout>();
  const lastToastRef = React.useRef<{
    message: string;
    type: string;
    time: number;
  }>();

  const showToast = React.useCallback(
    (
      message: string,
      type: "success" | "error" | "info" | "warning",
      options?: ToastOptions
    ) => {
      const now = Date.now();
      if (
        lastToastRef.current &&
        lastToastRef.current.message === message &&
        lastToastRef.current.type === type &&
        now - lastToastRef.current.time < 2000
      ) {
        return;
      }

      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      toastTimeoutRef.current = setTimeout(() => {
        toast[type](message, options);
        lastToastRef.current = { message, type, time: now };
      }, 100);
    },
    [toast]
  );

  return {
    success: React.useCallback(
      (message: string, options?: ToastOptions) =>
        showToast(message, "success", options),
      [showToast]
    ),
    error: React.useCallback(
      (message: string, options?: ToastOptions) =>
        showToast(message, "error", options),
      [showToast]
    ),
    info: React.useCallback(
      (message: string, options?: ToastOptions) =>
        showToast(message, "info", options),
      [showToast]
    ),
    warning: React.useCallback(
      (message: string, options?: ToastOptions) =>
        showToast(message, "warning", options),
      [showToast]
    ),
  };
};
